import { Trash, Package, Wrench, Desktop, CheckCircle, Broom, User, MapPin, Circuitry, Armchair } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { STOCK_MODES, ENG_MODES } from './scannerApi';
import { formatDate } from '@/utils/datetime';

function AssetIcon({ kind }) {
  if (kind === 'it') return <Desktop size={20} weight="duotone" className="text-violet-500 shrink-0" />;
  if (kind === 'eng') return <Circuitry size={20} weight="duotone" className="text-teal-500 shrink-0" />;
  if (kind === 'bihin') return <Armchair size={20} weight="duotone" className="text-emerald-500 shrink-0" />;
  return <Wrench size={20} weight="duotone" className="text-orange-500 shrink-0" />;
}

const fmtDate = (s) => {
  if (!s) return null;
  try { return formatDate(s); } catch { return null; }
};

export default function ScannerCart({ mode, cart, onUpdate, onRemove, onClear, onCommit, committing }) {
  const { t } = useT();
  const isStock = STOCK_MODES.includes(mode);
  const isEng = ENG_MODES.includes(mode);
  // Commit gating per mode:
  //   stock_in/out — quantity > 0 for all lines
  //   stocktake — counted_qty filled for all lines
  //   eng_use/return/scrap — quantity > 0 (scrap also needs a reason)
  //   bihin_stock — non-zero delta
  //   move/verify/lookup/assign/lend/return — any lines in cart
  const canCommit = cart.length > 0 && !committing
    && (
      isStock ? cart.every((l) => (mode === 'stocktake' ? l.counted_qty !== '' && l.counted_qty != null : Number(l.quantity) > 0))
      : isEng ? cart.every((l) => Number(l.quantity) > 0 && (mode !== 'eng_scrap' || (l.reason || '').trim()))
      : mode === 'bihin_stock' ? cart.every((l) => Number(l.quantity) !== 0 && l.quantity !== '')
      : true
    );

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm flex flex-col" data-testid="scanner-cart">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
        <h2 className="font-bold text-zinc-900 flex items-center gap-2">
          {t('scanner.cart.title')}
          <span className="text-xs font-semibold text-zinc-400" data-testid="scanner-cart-count">{cart.length}{t('scanner.itemsUnit')}</span>
        </h2>
        {cart.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} data-testid="scanner-cart-clear" className="h-8 text-zinc-500 hover:text-rose-600">
            <Broom size={15} className="mr-1" /> {t('scanner.cart.clear')}
          </Button>
        )}
      </div>

      <div className="divide-y divide-zinc-200 max-h-[46vh] overflow-y-auto">
        {cart.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-zinc-400" data-testid="scanner-cart-empty">{t('scanner.cart.empty')}</p>
        )}
        {cart.map((l) => (
          <div key={l.key} className="px-4 py-3 flex items-start gap-3" data-testid={`scanner-cart-line-${l.key}`}>
            {isStock ? <Package size={20} weight="duotone" className="text-cyan-500 shrink-0 mt-0.5" /> : <AssetIcon kind={l.asset_kind} />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-800 truncate">{l.name}</p>
              <p className="text-[11px] text-zinc-400 font-mono truncate">
                {isStock ? (l.isLot ? l.lot_no : l.sku) : l.asset_no}
              </p>
              {/* Asset detail chips for lookup/assign/verify/move — quick context */}
              {!isStock && (mode === 'lookup' || mode === 'assign' || mode === 'verify' || mode === 'move') && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {l.assigned_to_name && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                      <User size={10} weight="bold" /> {t('scanner.assignedTo')}: {l.assigned_to_name}
                    </span>
                  )}
                  {l.location && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200">
                      <MapPin size={10} weight="bold" /> {l.location}
                    </span>
                  )}
                  {l.last_verified_at && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                      {t('scanner.lastVerified')}: {fmtDate(l.last_verified_at)}
                    </span>
                  )}
                  {l.asset_kind === 'eng' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200">
                      在庫 {l.qty_available ?? 0} ・ 使用中 {l.qty_used ?? 0}
                    </span>
                  )}
                  {l.asset_kind === 'bihin' && l.bihin_kind === 'consumable' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      在庫 {l.stock_qty ?? 0}{l.uom || ''}
                    </span>
                  )}
                  {l.asset_kind === 'bihin' && l.lent_out && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
                      {t('scanner.lentTo')}{l.lent_to_name ? `: ${l.lent_to_name}` : ''}
                    </span>
                  )}
                </div>
              )}
              {/* Verify note input */}
              {mode === 'verify' && (
                <Input
                  data-testid={`scanner-verify-note-${l.key}`}
                  value={l.note || ''}
                  onChange={(e) => onUpdate(l.key, { note: e.target.value })}
                  placeholder="メモ（任意）"
                  className="h-8 text-xs mt-1.5"
                />
              )}
              {/* Scrap reason input (required) */}
              {mode === 'eng_scrap' && (
                <Input
                  data-testid={`scanner-scrap-reason-${l.key}`}
                  value={l.reason || ''}
                  onChange={(e) => onUpdate(l.key, { reason: e.target.value })}
                  placeholder="廃棄理由（必須）"
                  className="h-8 text-xs mt-1.5"
                />
              )}
            </div>

            {/* Stock in / out qty */}
            {(mode === 'stock_in' || mode === 'stock_out') && (
              <div className="flex items-center gap-2 shrink-0">
                {mode === 'stock_out' && <span className="text-[11px] text-zinc-400">{t('scanner.cart.onHand')} {l.on_hand}</span>}
                <Input type="number" min="0" value={l.quantity} data-testid={`scanner-qty-${l.key}`}
                  onChange={(e) => onUpdate(l.key, { quantity: e.target.value })}
                  className="h-10 w-20 text-right text-base" />
                <span className="text-xs text-zinc-500 w-6">{l.uom}</span>
              </div>
            )}

            {/* Stocktake counted */}
            {mode === 'stocktake' && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-zinc-400">{t('scanner.cart.onHand')} {l.isLot ? l.lot_quantity : l.on_hand}</span>
                <Input type="number" min="0" value={l.counted_qty} data-testid={`scanner-counted-${l.key}`}
                  onChange={(e) => onUpdate(l.key, { counted_qty: e.target.value })}
                  className="h-10 w-20 text-right text-base" />
                <span className="text-xs text-zinc-500 w-6">{l.uom}</span>
              </div>
            )}

            {/* 技術資産 use / return / scrap quantity */}
            {ENG_MODES.includes(mode) && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-zinc-400">
                  {mode === 'eng_return' ? `使用中 ${l.qty_used ?? 0}` : `在庫 ${l.qty_available ?? 0}`}
                </span>
                <Input type="number" min="0" value={l.quantity} data-testid={`scanner-qty-${l.key}`}
                  onChange={(e) => onUpdate(l.key, { quantity: e.target.value })}
                  className="h-10 w-20 text-right text-base" />
                <span className="text-xs text-zinc-500 w-6">{l.uom}</span>
              </div>
            )}

            {/* 備品 consumable stock delta */}
            {mode === 'bihin_stock' && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-zinc-400">在庫 {l.stock_qty ?? 0}</span>
                <Input type="number" value={l.quantity} data-testid={`scanner-delta-${l.key}`}
                  onChange={(e) => onUpdate(l.key, { quantity: e.target.value })}
                  className="h-10 w-20 text-right text-base" />
                <span className="text-xs text-zinc-500 w-6">{l.uom}</span>
              </div>
            )}

            {/* Asset status (lend/return) */}
            {(mode === 'lend' || mode === 'return') && (
              <div className="shrink-0">
                {l.lent_out
                  ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t('scanner.lentTo')}{l.lent_to_name ? `: ${l.lent_to_name}` : ''}</Badge>
                  : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle size={12} className="mr-1" />OK</Badge>}
              </div>
            )}

            <button onClick={() => onRemove(l.key)} data-testid={`scanner-remove-${l.key}`}
              className="shrink-0 text-zinc-300 hover:text-rose-500 transition-colors p-1">
              <Trash size={17} />
            </button>
          </div>
        ))}
      </div>

      {/* Commit button hidden in lookup — that mode is view-only per line, but a commit
          just records the audit trail. Keep the button but relabel it. */}
      <div className="px-4 py-3 border-t border-zinc-200">
        <Button onClick={onCommit} disabled={!canCommit} data-testid="scanner-commit-btn"
          className={`w-full h-12 text-base disabled:opacity-40 ${
            mode === 'lookup' ? 'bg-slate-600 hover:bg-slate-700' : 'bg-teal-700 hover:bg-teal-800'
          }`}>
          <CheckCircle size={18} weight="bold" className="mr-2" />
          {mode === 'lookup' ? '記録して閉じる' : t('scanner.cart.commit')}
        </Button>
      </div>
    </div>
  );
}
